/**
 * `AnalyticsCardShell` — ONE component, not three features.
 *
 * Every card on the Analytics canvas (stat tile or panel) is wrapped in this. It owns
 * the drag affordance, the expand control and the remove control, and — in
 * `surface="shell"` mode — the card surface and its header too. Drag, expand, remove and
 * resize are therefore one consistent affordance set rather than four bolted onto
 * whichever card needed each of them first.
 *
 * ## Two surface modes, because the existing cards already draw their own
 *
 * `AnalyticsBlock`, `ScansByTime2` and `ScansByLocation` each render their OWN
 * `@repo/ui` `Card`. A shell that unconditionally added one would produce a card
 * inside a card. So:
 *
 * | mode | who draws the `Card` + header | controls |
 * |---|---|---|
 * | `shell` (default for panels) | THIS component, via `CardSectionHeader` | in the header's action slot |
 * | `own` (default for tiles) | the child | floated over the child's top-right, revealed on hover/focus |
 *
 * `own` exists so the shell can be adopted with NO edit to the card it wraps — which
 * is what makes tiles work today, since `AnalyticsBlock` is shared with Tags ->
 * Inventory and Billing and must not grow an analytics-only header.
 *
 * ## The drag surface: the HEADER, plus a visible grip INSIDE it
 *
 * The header is the drag surface, which is what Grafana, Datadog, Metabase and Notion
 * all do — a dashboard card's title bar is the one strip guaranteed to contain no
 * content the user wants to select or click. That alone is not DISCOVERABLE, though:
 * nothing on screen says the header is draggable, so the affordance is invisible
 * until someone tries it by accident. Hence both — the header is the surface, and a
 * grip icon appears on hover and on `:focus-within` to announce it.
 *
 * The grip is de-emphasised rather than fully drawn because a canvas of eight cards
 * each displaying a grip at full weight reads as chrome, not as data. How far it is
 * de-emphasised depends on whether it reserves layout space — see
 * `CONTROLS_REST_OPACITY`, which is where the "odd gap on hover" was fixed.
 * `:focus-within` is the keyboard half and is NOT optional: without it the grip and
 * the expand button are reachable by Tab while invisible.
 *
 * Which element is the drag surface differs by kind, and the split is deliberate:
 *
 * | kind | drag surface | why |
 * |---|---|---|
 * | panel | the header row only | a 700px chart's BODY must stay interactive — brush, tooltip, map pan, text selection |
 * | tile | the whole card | a 160px card showing one number has no interior interaction to protect, and its "header" is its title, which is most of it |
 *
 * Interactive descendants are excluded by `handleDragStart`'s guard against
 * `ANALYTICS_DRAG_CANCEL_SELECTOR`, so a card's own header control (the map's grouping
 * toggle) still receives its clicks rather than starting a drag. The grip is a plain `Box`
 * and NOT a `ButtonBase` precisely so that the selector does not match it — otherwise the
 * one control advertising the gesture would be the one control unable to start it.
 *
 * ## The expand affordance, and why its icon rule differs from a state toggle's
 *
 * A state toggle shows the CURRENT state and names the ACTION in its tooltip, because
 * two static pictures are genuinely ambiguous about which one is being shown. Expand
 * has no such ambiguity: `Maximize2` and `Minimize2` are DIRECTIONAL — arrows point
 * outward or inward — so the glyph already reads as a verb. Icon and tooltip both
 * name the action, and the machine-readable state lives on `aria-expanded`.
 *
 * ## Expansion is a region that grew, NOT a dialog
 *
 * Deliberately no `aria-modal`, no focus trap, no backdrop, no portal: the expanded
 * card is still in the document flow, below the action line, inside the page gutter.
 * A focus trap would be a lie about the page's structure and would strand keyboard
 * users behind a control they never opened. Escape collapses; focus returns to the
 * expand button that opened it.
 *
 * ## ONE DRAG (VERA-260) — the drop TARGET decides what it does
 *
 * `AnalyticsCanvas`'s compose-drop gesture (VRG-467) already let a LIBRARY row be
 * dragged onto an existing chart to join it as a series. VERA-260 added the other
 * direction: a metric already ON THE CANVAS — a KPI tile, or a single-metric chart/map
 * panel — can be dragged onto a DIFFERENT chart to merge into it too.
 *
 * There is now exactly one gesture on the canvas, and one affordance model:
 *
 * | you release on | what happens |
 * |---|---|
 * | empty grid (or another tile) | the card MOVES there |
 * | a chart or map that can take this measure | the card MERGES into it as a series |
 *
 * Everything about the pick-up is identical either way. The grip and the card's own drag
 * surface are the same element's handler (`dragSurfaceProps`), on the same protocol,
 * producing the same drag image; the grip exists purely so the gesture is discoverable,
 * and appears on EVERY card.
 *
 * ### ⚠️ Two earlier arrangements, and why both were wrong
 *
 * **Two icons.** It shipped first as a grip that repositioned the card and a `+` beside it
 * that merged it. That asked the user a question they cannot answer at the moment of
 * asking — whether a drag merges or moves is decided by where you RELEASE — and picking
 * the "wrong" icon made the other outcome unreachable without dropping and starting over.
 *
 * **Two protocols, split by surface.** It then became: the GRIP merges (native HTML5
 * drag-and-drop), the card's SURFACE moves (react-grid-layout's pointer drag). Better, but
 * the same defect one level down — the two felt like different gestures because they were,
 * and the grip only appeared on cards that could merge, so a card like Completed Transfers
 * had no drag affordance at all despite being movable.
 *
 * That split was held in place by a finding that turned out to be a measurement error: that
 * a native drop could compute a correct reposition but never get it DRAWN, because
 * `ResponsiveGridLayout` supposedly would not adopt a `layouts` prop change originating
 * from its own `onDrop`. It does adopt it. `AnalyticsCanvas.handleDrop` carries the
 * re-measurement and the explanation of what the original reading actually caught.
 *
 * ### ⚠️ WHAT REMAINS TRUE: the two protocols still cannot share an element
 *
 * Adding `draggable` to an element react-grid-layout also treats as a drag handle engages
 * BOTH: its 3px threshold always wins the start (Chrome's native one is ~5px), and the
 * native drag then suppresses `mouseup` for its whole duration, so react-draggable never
 * sees a terminator and never runs `onDragStop`. The card is left stranded — still
 * `.react-draggable-dragging`, transform applied, nothing committed. No ordering fixes it,
 * because each protocol wins a different END of the gesture.
 *
 * So unification meant CHOOSING one, not layering them: react-grid-layout's dragging is
 * switched off (`AnalyticsCanvas.DRAG_CONFIG`) and the whole canvas is native, because
 * native is the only protocol with a concept of a drop TARGET and the target is what
 * decides the outcome. Everything else the library does — geometry, compaction, the drop
 * placeholder and its live re-flow preview, resize — is untouched.
 *
 * ### ⚠️ THE COST: reposition no longer works by TOUCH
 *
 * HTML5 drag-and-drop has no touch implementation. Merging never worked on a tablet either
 * (it has always been native), so what is lost is reposition, on a surface that is
 * desktop-only by convention (working-conventions §37) and which also offers drag-to-resize
 * handles that were never touch-friendly. Restoring it means a touch shim that synthesises
 * drag events from pointer events, or `dragConfig` re-enabled only under a coarse-pointer
 * media query — not re-splitting the gesture, which is the thing this change exists to end.
 *
 * ### ⚠️ THE TOOLTIP DOES NOT DESCRIBE THE GESTURE'S SCOPE — the canvas does
 *
 * The handle once read **"Drag to add to another chart"**, and the principle behind
 * cutting that sentence applies to every affordance on this page: **a tooltip EXPLAINS a
 * capability; a highlight DEMONSTRATES its scope.** The second is strictly more
 * informative and needs no reading — it names not just that targets exist but WHICH ones,
 * and (via the refused overlay) which ones cannot take this particular measure and why.
 * "Another chart" could never say any of that; at best it was a promise the canvas then
 * had to keep, and at worst it was simply wrong, since on a canvas holding one chart there
 * IS no other chart to add to.
 *
 * 🚨 And it was not merely redundant, it COMPETED. Measured in the running app: a native
 * HTML5 drag fires no `mousemove`/`mouseleave` for its whole duration, so MUI's tooltip
 * has nothing to close on — starting a drag with the tooltip open leaves it pinned to the
 * (by then scrolled-away) handle's anchor, floating in the page chrome, for as long as the
 * drag lasts. The reader is looking at the canvas and being handed a sentence somewhere
 * else. That is the exact failure the highlight exists to end.
 *
 * So the grip's tooltip names the VERB and, when a merge is on offer, the fact that a
 * chart is a place you can release onto — **"Drag to move, or onto a chart to add"** —
 * and nothing more. WHICH charts is left entirely to the canvas, which answers it the
 * moment the drag begins. And the tooltip is CONTROLLED and closed on `dragstart`
 * (`gripTipOpen` below), which is what actually stops it competing; closing on
 * `dragstart` alone is NOT sufficient, because `onOpen` fires on a delay timer and a fast
 * grab re-opens it afterwards — `gripDraggingRef` is the half that makes the close stick,
 * and both halves were needed to fix the observed behaviour.
 *
 * The `aria-label` keeps the FULLER sentence. A screen-reader user gets no highlight at
 * all, so for them the prose is not redundant — it is the only description there is.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { GripVerticalIcon, Maximize2Icon, Minimize2Icon, XIcon } from 'lucide-react'

import { Card, CONTROL_HEIGHT } from '@repo/ui'
import { CardSectionHeader } from 'components/display/CardSectionHeader'

import {
  ANALYTICS_DRAG_CANCEL_SELECTOR,
  ANALYTICS_DRAG_HANDLE_CLASS,
  ANALYTICS_GRIP_CLASS,
} from './analyticsGrid'

export type AnalyticsCardKind = 'tile' | 'panel'
export type AnalyticsCardSurface = 'shell' | 'own'

export interface AnalyticsCardShellProps {
  /** Stable id. Also the grid id and the expand key — one identity, not three. */
  id: string
  /** Used by `surface="shell"`; otherwise only for the controls' accessible names. */
  title?: string
  description?: React.ReactNode
  /** The card's OWN trailing control (legend, segmented toggle, link). `shell` only. */
  action?: React.ReactNode
  /**
   * Tiles are 160px and read as a row compared in one glance; panels are 500px+ and
   * read as one thing you study. The kind drives the min height, the defaults for
   * both `surface` and `expandable`, and where the drag handle class lands.
   */
  kind?: AnalyticsCardKind
  surface?: AnalyticsCardSurface
  /** False while a card is maximized — there is nothing to drag it relative to. */
  draggable?: boolean
  /**
   * Offer the expand control. Defaults to true for panels and FALSE for tiles: a
   * 160px tile showing one number has nothing to expand into, and filling the region
   * with a scaled-up figure would be inventing content to fill space.
   */
  expandable?: boolean
  expanded?: boolean
  onToggleExpand?: (id: string, next: boolean) => void
  /**
   * Take focus as soon as this card's expand control exists.
   *
   * The canvas sets it after a collapse. It is a PROP rather than something this
   * component works out for itself because the maximized card and the gridded card
   * are two different instances: the one holding the control the user pressed has
   * already unmounted by the time the collapse renders, so it has nothing to focus
   * and no way to know a collapse happened.
   *
   * It also cannot be a `querySelector` from the canvas, which was tried and read
   * `document.activeElement === BODY` afterwards: react-grid-layout gates its own
   * children behind an internal `mounted` state, so they appear a commit LATER than
   * the collapse and the element does not exist when the canvas's effect runs. Doing
   * it on this component's own mount is the only point that is guaranteed to be after
   * the button is in the DOM.
   */
  autoFocusExpandControl?: boolean
  /** Fired once focus has been taken, so the canvas can clear its request. */
  onExpandControlFocused?: () => void
  /**
   * Take this card OFF the canvas. Omitted -> no remove control.
   *
   * ## `X`, and no confirmation
   *
   * `X` rather than a trash can because nothing is destroyed (§38): the card leaves a
   * set, the metric and its data are untouched, and the row is still sitting in the
   * Layout panel one click away. A trash glyph would claim a consequence the action
   * does not have.
   *
   * No confirm dialog either, and that is a deliberate call rather than an omission.
   * §43's test for a gate is whether harm accrues that cannot be recovered — here none
   * does, and re-adding restores the card's POSITION too (`useAnalyticsView.addCard`),
   * so the undo is complete rather than approximate. Confirming a cheap, frequent,
   * fully reversible action is what spends the user's tolerance for confirmations on
   * nothing, so that the one guarding a real consequence gets clicked through.
   */
  onRemove?: (id: string) => void
  /**
   * VERA-260 — can THIS card also be dropped onto a different chart/map to merge into
   * it? Computed by the canvas per card (`isComposeSource`), not by this component —
   * the shell has no access to `analyticsDisplayCapability` or the id grammar, and
   * should not need it to decide what a tooltip says.
   *
   * ⚠️ IT GATES NO AFFORDANCE AT ALL. It first drew a second `+` handle beside the grip;
   * then it gated whether the card got a grip. It now changes only the WORDING of the
   * grip's tooltip, the surface's `title` and the accessible name — whether they mention
   * adding to a chart. A card that cannot compose is dragged exactly the same way and by
   * exactly the same affordance; it just only ever moves.
   *
   * Gating the grip on it was the bug this closes: a card whose metric no chart can plot
   * had no visible drag affordance whatsoever, despite being perfectly movable.
   */
  composeSourceEnabled?: boolean
  /**
   * Fired on the grip's native `dragstart` / `dragend`.
   *
   * `dragend` fires whether or not the drop landed anywhere, so it is the reliable place
   * to clear drag state; `drop` is not, because a drag released outside the grid never
   * produces one.
   */
  onCardDragStart?: (id: string) => void
  onCardDragEnd?: () => void
  children: React.ReactNode
}

/**
 * Height of the grip and the expand button.
 *
 * `CONTROL_HEIGHT` (40), the shared band token — which is specifically the height of
 * a `SegmentedTabs` PILL, the control that sits beside them in the map card's header.
 * `SegmentedTabs`' own source records the reasoning: the thing a reader perceives as
 * "the control" is the pill, not the 48px track it is recessed into, and the pill was
 * moved to 40 precisely so it would match the button next to it. Matching the track
 * instead would make the expand button the tallest thing in the header.
 *
 * A default MUI `IconButton` is ~34px (`size="small"` is ~30), which is where the
 * mismatch came from.
 */
const CONTROL_SIZE = CONTROL_HEIGHT

/** Panels get a floor so an empty chart does not collapse to its header. */
const PANEL_MIN_HEIGHT = 320

/**
 * What the drag DOES, in the two shapes a card can be.
 *
 * The wording is the only thing `composeSourceEnabled` still changes. Every card can be
 * moved, so every card says so; a card whose metric a chart could actually plot adds the
 * second outcome, because for that card the same gesture has two endings and the reader
 * cannot discover the second one by looking.
 *
 * ⚠️ It names the outcomes and stops there. WHICH charts will take this metric is left
 * entirely to the canvas, which answers it the moment the drag starts by lighting up
 * every eligible target and explaining every refusal — see `AnalyticsCanvas`'s overlay
 * headers, and this file's own header for why a sentence competing with those highlights
 * is worse than no sentence.
 */
const DRAG_TITLE_MOVE_ONLY = 'Drag to move'
const DRAG_TITLE_MOVE_OR_COMPOSE = 'Drag to move, or onto a chart to add'

/**
 * The announcement on the drag SURFACE — a native `title`, deliberately not a MUI
 * `Tooltip`.
 *
 * The element carrying it is the whole card's drag surface (a tile's entire body, a
 * panel's whole header row), and a styled tooltip on a surface that large is intrusive:
 * it follows every pass of the pointer across the card and competes with the card's own
 * content and its info icons. `title` waits for a real hover pause, renders as OS chrome
 * rather than as another floating panel, and — the part that matters here — the browser
 * suppresses it for the whole duration of a drag, so it cannot pin itself over the canvas
 * the way a MUI tooltip on a native drag source does (see this file's header).
 */
const dragSurfaceTitle = (offersCompose: boolean) =>
  offersCompose ? DRAG_TITLE_MOVE_OR_COMPOSE : DRAG_TITLE_MOVE_ONLY

/**
 * Rest opacity of the controls, and why it differs by surface mode.
 *
 * The reveal was `0 -> 1` in both modes. Measured, that causes ZERO layout shift in
 * either — the controls keep their box at opacity 0, so a card's header height, its
 * title position and the controls' own width are byte-identical hovered and not (all
 * six cards, opacity forced 0 vs 1: 160/160, 78/78, 120/120 header heights, titles at
 * the same x and y to two decimals). The complaint was never a reflow.
 *
 * It was the HOLE. In `shell` mode the controls sit IN the header's action row, so at
 * opacity 0 they leave 92px of reserved-but-empty space plus a 12px gap between the
 * card's own control and the card's right edge — a conspicuous notch that appears to be
 * a layout mistake right up until you hover it. So the space stays reserved (that is
 * what keeps the shift at zero) and something occupies it: the controls themselves, at
 * a weight low enough that a canvas of panels still reads as data.
 *
 * `own` mode keeps the full `0 -> 1` reveal, because there is no hole to fill — those
 * controls are absolutely positioned OVER a 160px stat tile, reserve no space, and at
 * any resting opacity would sit on top of the tile's own title.
 */
const CONTROLS_REST_OPACITY: Record<AnalyticsCardSurface, number> = { shell: 0.45, own: 0 }

/**
 * Skipped under `prefers-reduced-motion`. A 120ms cross-fade is about as small as a
 * transition gets, but the setting is a request about motion in general and honouring
 * it selectively is how a page ends up with three that were each judged "fine".
 */
const CONTROLS_TRANSITION = {
  transition: 'opacity 120ms ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const

export const AnalyticsCardShell = ({
  id,
  title,
  description,
  action,
  kind = 'panel',
  surface,
  draggable = true,
  expandable,
  expanded = false,
  onToggleExpand,
  autoFocusExpandControl = false,
  onExpandControlFocused,
  onRemove,
  composeSourceEnabled = false,
  onCardDragStart,
  onCardDragEnd,
  children,
}: AnalyticsCardShellProps) => {
  const expandButtonRef = useRef<HTMLButtonElement | null>(null)
  /**
   * The grip's tooltip, CONTROLLED so `dragstart` can close it.
   *
   * ⚠️ Not a style preference — see this file's header. A native HTML5 drag suppresses
   * `mousemove`/`mouseleave` for its whole duration, so an uncontrolled tooltip has no
   * event to close on and stays pinned to its anchor over the canvas while the reader is
   * trying to read the canvas's own drop overlays. MUI still owns open/close on hover and
   * focus via `onOpen`/`onClose`; this only adds the transitions MUI cannot see.
   *
   * This machinery moved here from the retired `+` handle unchanged. It matters MORE now,
   * not less: the grip is on every card, so an unclosed tooltip would be reachable from
   * every drag rather than only from a compose-capable one.
   */
  const [gripTipOpen, setGripTipOpen] = useState(false)
  /**
   * ⚠️ A ref, not state, and it is what makes the close above actually STICK.
   *
   * `Tooltip.onOpen` does not fire on `mouseenter` — it fires on a timer, after
   * `enterDelay` (100ms). So a reader who grabs the grip quickly starts the drag INSIDE
   * that window, `dragstart` sets `gripTipOpen` false while the open timer is still
   * pending, and the timer then fires and re-opens the tooltip over a canvas that is
   * already showing its drop overlays — the precise failure this was meant to remove,
   * surviving in the fast-grab case. Observed in the running app: with only the
   * `dragstart` close, a hover-then-immediate-drag left the tooltip mounted for the rest
   * of the gesture, while a hover-pause-then-drag closed correctly.
   *
   * A ref rather than state because it is read inside `onOpen` to decide whether an open
   * is still wanted; routing it through a re-render would reintroduce the same ordering
   * question one level up. Cleared on `dragend`, which fires whether or not the drop
   * landed, so a cancelled drag leaves the handle able to show its tooltip again.
   */
  const gripDraggingRef = useRef(false)
  const mode: AnalyticsCardSurface = surface ?? (kind === 'panel' ? 'shell' : 'own')
  const canExpand = (expandable ?? kind === 'panel') && Boolean(onToggleExpand)
  const canDrag = draggable && !expanded
  /** Does the grip's drag ALSO offer a merge, or only a move? Wording only — see the prop. */
  const gripOffersCompose = composeSourceEnabled && !expanded

  const collapse = useCallback(() => onToggleExpand?.(id, false), [id, onToggleExpand])

  // Escape collapses. Bound to the document rather than the card because the pointer
  // may be anywhere by the time the user reaches for Escape, and the expanded card
  // covers the region either way. `defaultPrevented` is respected so a menu, popover
  // or date picker opened INSIDE the card consumes Escape first and closes itself —
  // otherwise one keypress would close both.
  useEffect(() => {
    if (!expanded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      collapse()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [expanded, collapse])

  // Focus restoration after a collapse. See `autoFocusExpandControl`.
  useEffect(() => {
    if (!autoFocusExpandControl || !expandButtonRef.current) return
    expandButtonRef.current.focus()
    onExpandControlFocused?.()
  }, [autoFocusExpandControl, onExpandControlFocused])

  /**
   * ── THE CARD'S ONE DRAG, wherever on the card it was started ────────────────────────
   *
   * These handlers go on the SURFACE (`ANALYTICS_DRAG_HANDLE_CLASS` — a tile's body, a
   * panel's header row). The grip sits INSIDE that element and carries no `draggable` of
   * its own, so grabbing the grip and grabbing the card are not two behaviours that happen
   * to agree; they are one handler reached two ways. That is the only way "the grip behaves
   * identically to grabbing the card" can be true by construction rather than by upkeep.
   *
   * `setData` mirrors `AnalyticsCustomize.handleDragStart`: Firefox refuses to start a
   * native drag at all unless something is set.
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      /*
        ── ⚠️ THE GUARD THAT REPLACES `dragConfig.cancel` ────────────────────────────────

        Without it a card's own controls stop working the way they read: `draggable` on an
        ancestor means a press-and-drag on the map's grouping toggle, the expand button or
        the remove `X` picks the CARD up instead of operating the control. `preventDefault`
        on `dragstart` cancels the gesture outright, which is exactly what `cancel` did.

        The GRIP is deliberately not in that selector any more (see the constant): it is a
        plain `Box` rather than a `ButtonBase` precisely so it does not match, because the
        one control that advertises the gesture must be the one control that can start it.

        🚨 IT HIT-TESTS THE POINTER, NOT `event.target`, and that is not a style choice.
        Per the HTML drag-and-drop spec the `dragstart` event is fired at the SOURCE NODE —
        the nearest `draggable` ancestor — so `e.target` is this surface itself no matter
        which descendant the press actually landed on. A guard written against `e.target`
        compiles, reads correctly, and silently never fires. `elementFromPoint` asks the
        question that was actually meant: what is under the cursor right now? (The drag
        image does not exist yet at `dragstart`, so it cannot occlude the answer.)
      */
      const under =
        typeof document.elementFromPoint === 'function'
          ? document.elementFromPoint(e.clientX, e.clientY)
          : null
      if (under?.closest?.(ANALYTICS_DRAG_CANCEL_SELECTOR)) {
        e.preventDefault()
        return
      }

      e.dataTransfer.setData('text/plain', id)
      e.dataTransfer.effectAllowed = 'copy'
      /*
        The drag image is the whole CARD, not whichever part of it was grabbed.

        Found by climbing to the GRID CELL rather than held in a ref, because the two
        surface modes have different roots and `shell` mode's is the shared `@repo/ui`
        `Card`, which forwards neither `ref` nor any prop it does not name — a ref there
        would silently be dropped (its own source documents that trap). The grid cell is a
        better answer anyway: it is exactly the rectangle the card occupies, in both modes,
        and it is the thing the drop is about to move.

        Offset so the card hangs off the cursor at roughly the point that was grabbed,
        rather than snapping its top-left corner under the pointer — which for a 1151px-wide
        panel would put the whole card off to the right of where the reader is aiming.
        Guarded because `setDragImage` is absent in some environments and a throw here would
        abort the drag entirely.
      */
      const node =
        e.currentTarget.closest<HTMLElement>('.react-grid-item') ??
        e.currentTarget.closest<HTMLElement>('[data-analytics-card]')
      if (node && typeof e.dataTransfer.setDragImage === 'function') {
        const rect = node.getBoundingClientRect()
        e.dataTransfer.setDragImage(
          node,
          Math.max(0, Math.min(rect.width, e.clientX - rect.left)),
          Math.max(0, Math.min(rect.height, e.clientY - rect.top)),
        )
      }
      // Both BEFORE `onCardDragStart`, so the tooltip is already closed — and cannot be
      // re-opened by a pending enter timer — in the same commit that paints the canvas's
      // drop overlays. The two must never be on screen together, which is the whole point
      // of controlling the tooltip at all.
      gripDraggingRef.current = true
      setGripTipOpen(false)
      onCardDragStart?.(id)
    },
    [id, onCardDragStart],
  )

  const handleDragEnd = useCallback(() => {
    gripDraggingRef.current = false
    onCardDragEnd?.()
  }, [onCardDragEnd])

  /**
   * `draggable` plus its handlers, spread onto whichever element is this card's drag
   * surface. One object so the two surface modes cannot drift apart.
   *
   * ⚠️ `onDragEnd` is what clears the canvas's drag state, and it must stay on the surface
   * rather than the grid: it fires whether or not the drop landed anywhere, while `drop`
   * does not fire at all for a drag released outside the grid.
   */
  const dragSurfaceProps = canDrag
    ? {
        className: ANALYTICS_DRAG_HANDLE_CLASS,
        draggable: true,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        title: dragSurfaceTitle(gripOffersCompose),
      }
    : {}

  /**
   * ── THE GRIP — pure discoverability, on EVERY card ──────────────────────────────────
   *
   * It starts no gesture of its own. It is a labelled patch of the drag surface, and its
   * whole job is that a card should not have to be grabbed by accident to be found
   * draggable — nothing else on a card says it can be picked up.
   *
   * ## ⚠️ It is no longer gated on `composeSourceEnabled`, and that is the fix
   *
   * The grip used to appear only on cards that could merge into a chart, because it WAS
   * the merge handle — a card that could not merge got no grip, and its only remaining cue
   * that it could be moved was the `grab` cursor. So Completed Transfers and In Progress
   * Transfers, whose metrics no chart can plot, showed no drag affordance whatsoever
   * despite being perfectly movable. With one gesture there is one affordance, and every
   * card gets it; `composeSourceEnabled` now changes only the WORDING (see
   * `dragSurfaceTitle`), because the second outcome is the one a reader cannot guess.
   *
   * A `Box`, not an `IconButton` — see `handleDragStart`'s guard. A `ButtonBase` here would
   * match `ANALYTICS_DRAG_CANCEL_SELECTOR` and the grip would be the one part of the card
   * that could NOT start the drag it advertises.
   */
  const grip = canDrag ? (
    <Tooltip
      title={dragSurfaceTitle(gripOffersCompose)}
      open={gripTipOpen}
      // ⚠️ The guard is not belt-and-braces — `onOpen` fires on the `enterDelay` TIMER,
      // so without it a fast grab re-opens the tooltip AFTER `dragstart` closed it. See
      // `gripDraggingRef`.
      onOpen={() => {
        if (!gripDraggingRef.current) setGripTipOpen(true)
      }}
      onClose={() => setGripTipOpen(false)}
    >
      <Box
        className={ANALYTICS_GRIP_CLASS}
        // The accessible name keeps the FULLER sentence: a screen-reader user gets no
        // highlight from the canvas at all, so for them the prose is not redundant, it is
        // the only description of the gesture there is.
        aria-label={
          gripOffersCompose
            ? `Drag ${title ?? id} to move it, or onto another chart to add it`
            : `Drag ${title ?? id} to move it`
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: CONTROL_SIZE,
          height: CONTROL_SIZE,
          borderRadius: 2,
          cursor: 'grab',
          color: 'text.disabled',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { color: 'text.secondary', backgroundColor: 'action.hover' },
        }}
      >
        <GripVerticalIcon size={18} strokeWidth={1.5} />
      </Box>
    </Tooltip>
  ) : null

  const expandControl = canExpand ? (
    <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
      <IconButton
        ref={expandButtonRef}
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${title ?? id}` : `Expand ${title ?? id}`}
        onClick={() => onToggleExpand?.(id, !expanded)}
        // Explicit box, not `size`: the shared band is 40 and MUI's `medium` is 40 for
        // the ripple but not for the rendered button. See CONTROL_SIZE.
        sx={{ width: CONTROL_SIZE, height: CONTROL_SIZE, borderRadius: 2, color: 'text.secondary' }}
      >
        {expanded ? (
          <Minimize2Icon size={16} strokeWidth={1.5} />
        ) : (
          <Maximize2Icon size={16} strokeWidth={1.5} />
        )}
      </IconButton>
    </Tooltip>
  ) : null

  /**
   * Remove — the card leaves the canvas.
   *
   * Withheld while `expanded`, for the same reason `draggable` is: the expanded card IS
   * the content region, so removing it would empty the region the user is looking at and
   * leave them facing a blank page with no account of what happened. Collapse is the
   * control that is on screen at that moment, and the remove is one click behind it.
   *
   * NOT `destructive` (§32) — this is set-removal, not deletion, and painting it red
   * would overstate it exactly as a trash can would.
   */
  const removeControl =
    onRemove && !expanded ? (
      <Tooltip title="Remove from dashboard">
        <IconButton
          aria-label={`Remove ${title ?? id} from the dashboard`}
          onClick={() => onRemove(id)}
          sx={{
            width: CONTROL_SIZE,
            height: CONTROL_SIZE,
            borderRadius: 2,
            color: 'text.secondary',
          }}
        >
          <XIcon size={16} strokeWidth={1.5} />
        </IconButton>
      </Tooltip>
    ) : null

  const hasControls = Boolean(grip || expandControl || removeControl)

  /**
   * Grip, expand, remove — 12px apart, and in that order.
   *
   * The ordering rule is CONSEQUENCE, increasing toward the card's edge, which is where
   * the eye and the cursor arrive last. The grip only moves the card (or merges it, which
   * is equally a no-op when the drop misses) and is recoverable by dragging it back;
   * expand is fully reversible and ephemeral; remove takes the card off the page. So
   * remove is outermost — and it is also where the Layout panel already puts its own
   * remove control on the matching row, so the two agree. A SUCCESSFUL merge does remove
   * the source card as a side effect (see `AnalyticsCanvas`'s `onComposeDrop` wiring) —
   * but that consequence is a property of where you DROP it, not of picking the grip up,
   * which is why it is ordered by the gesture's own risk (cancellable) rather than by its
   * most consequential outcome.
   *
   * ⚠️ BACK TO THREE. VERA-260 briefly made it four, by adding a `+` compose-source handle
   * beside the grip — one icon to move the card, a second to merge it. That split was
   * mechanical, not conceptual: both are "pick this card up and put it somewhere", and
   * which one you get is decided by where you RELEASE, which is not something a user can
   * know when choosing which icon to press. Two icons for one gesture also meant the
   * grip's tooltip promised "reorder" while the real answer was "reorder OR merge,
   * depending". The grip now owns the whole gesture and the `+` is gone, so this corner is
   * back inside the three-control ceiling — and a tile is back to two (grip + remove;
   * tiles keep `expandable: false`).
   *
   * The gap is §29's button-group 12px (`spacing={3}` on the platform's 4px base) — they
   * were flush before, which read as one two-headed control rather than two.
   *
   * ## The DIRECTION differs by surface mode, and it is a MEASUREMENT, not a preference
   *
   * `shell` lays them in a ROW, in the header's action slot, where there is a whole strip
   * to sit in. `own` lays them in a COLUMN, and that is the fix for a real collision:
   *
   * A tile has no header row, so its controls float over the card's top-right — which is
   * where the tile's TITLE also ends. Measured at 1280 with the default view (270px
   * tiles), horizontal clearance between a title's rightmost ink and a 92px two-control
   * ROW is:
   *
   *   Total Scans             +45.2px   fine
   *   In Progress Transfers    -2.4px   touching
   *   Consumer Touchpoints    -40.2px   the strip covers "…points" and the info icon
   *
   * The strip is opaque (`background.paper`), so the overlap does not garble the text —
   * it HIDES it, which is worse in a quieter way: hovering a tile made its title appear
   * truncated. A COLUMN is 40px wide instead of 92, which buys back 52px and puts the
   * worst case at +11.8px. It costs nothing else: a tile is 270 x 160 and everything
   * below its title row ends by x=90 (the stat at x=44, the delta line at x=90), so the
   * 40px column occupies a strip of the card that is empty at every height.
   *
   * The obvious alternatives were worse. Shrinking the controls breaks §29's 40px band.
   * Dropping the tile's grip would buy the same 52px but remove the only thing that
   * ANNOUNCES a tile is draggable. Reserving the width in the title row would mean
   * editing `AnalyticsBlock`, which Tags -> Inventory and Billing also render, to add
   * permanent dead space for a control only this page has.
   */
  const controls = hasControls ? (
    <Stack
      direction={mode === 'own' ? 'column' : 'row'}
      alignItems="center"
      spacing={3}
    >
      {grip}
      {expandControl}
      {removeControl}
    </Stack>
  ) : null

  // ---------------------------------------------------------------------------
  // `own` — the child draws its own Card and header; we float the controls, and the
  // WHOLE card is the drag handle.
  // ---------------------------------------------------------------------------
  if (mode === 'own') {
    return (
      <Box
        // A tile's whole body is the drag surface, so `draggable`, the handlers, the
        // `title` and the `grab` cursor all sit on this one element.
        {...dragSurfaceProps}
        sx={{
          position: 'relative',
          height: '100%',
          cursor: canDrag ? 'grab' : undefined,
          '&:active': canDrag ? { cursor: 'grabbing' } : undefined,
          // The controls are chrome, not content: hidden until the card is engaged, so
          // a row of stat tiles reads as figures rather than as figures plus two
          // buttons each.
          '&:hover .analytics-card-controls, &:focus-within .analytics-card-controls': {
            opacity: 1,
          },
        }}
      >
        {controls && (
          <Box
            className="analytics-card-controls"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              opacity: CONTROLS_REST_OPACITY.own,
              ...CONTROLS_TRANSITION,
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }}
          >
            {controls}
          </Box>
        )}

        <Box sx={{ height: '100%' }}>{children}</Box>
      </Box>
    )
  }

  // ---------------------------------------------------------------------------
  // `shell` — this component draws the Card and the header, and ONLY the header row
  // is the drag handle.
  // ---------------------------------------------------------------------------
  const headerAction =
    action || controls ? (
      <Stack direction="row" alignItems="center" spacing={3}>
        {action}
        {/* Dimmed at rest, not hidden — see CONTROLS_REST_OPACITY. `focus-within` is on
            the Card below, which is the smallest element containing both the controls
            and everything that can focus into them. */}
        <Box
          className="analytics-card-controls"
          sx={{ opacity: CONTROLS_REST_OPACITY.shell, ...CONTROLS_TRANSITION }}
        >
          {controls}
        </Box>
      </Stack>
    ) : undefined

  return (
    <Card
      // `fill` makes the Paper and its inner wrappers `flex:1; minHeight:0`, which is
      // what lets the card's body be bounded by its grid cell rather than overflowing
      // it — the property the whole resizable grid depends on.
      fill
      sx={{
        height: '100%',
        minHeight: expanded ? undefined : PANEL_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        '&:hover .analytics-card-controls, &:focus-within .analytics-card-controls': {
          opacity: 1,
        },
      }}
    >
      {(title || headerAction) && (
        <Box
          // On a panel the HEADER ROW is the drag surface, so the announcement belongs
          // here and not on the card — hovering a 700px chart to be told it can be dragged
          // by a strip at its top would be worse than silence. The chart's own body stays
          // free for brushing, tooltips, map panning and text selection, which is why the
          // surface is the header rather than the whole card.
          {...dragSurfaceProps}
          sx={{
            mb: 4,
            flexShrink: 0,
            cursor: canDrag ? 'grab' : undefined,
            '&:active': canDrag ? { cursor: 'grabbing' } : undefined,
          }}
        >
          <CardSectionHeader title={title ?? ''} description={description} action={headerAction} />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Card>
  )
}
